import { useState } from "react";
import { Anchor, Button, PasswordInput, Stack, TextInput } from "@mantine/core";
import type { OAuthProvider } from "@/lib/api/client";

const PROVIDER_ICONS: Record<string, string> = {
  google: "/icons/google.svg",
  microsoft: "/icons/microsoft.svg",
  apple: "/icons/apple.svg",
  linkedin: "/icons/linkedin.svg",
  discord: "/icons/discord.svg",
  slack: "/icons/slack.svg",
};

export interface LoginOptionsProps {
  providers: OAuthProvider[];
  /** Whether the email + password form has been revealed via the link. */
  passwordOpen: boolean;
  onRevealPassword: () => void;
  onOAuthSignIn: (provider: OAuthProvider) => void;
  onPasswordSubmit: (email: string, password: string) => void;
  isLoading: boolean;
}

/** Sign-in options for the member portal: OAuth provider buttons first, with
 *  the email + password form behind a "Sign in with password" link. With no
 *  provider enabled the password form is the only option, so it shows directly.
 *
 *  Hook-free and controlled (`passwordOpen` lives in the parent) so the reveal
 *  wiring can be unit-tested without a DOM. */
export function LoginOptions({
  providers,
  passwordOpen,
  onRevealPassword,
  onOAuthSignIn,
  onPasswordSubmit,
  isLoading,
}: LoginOptionsProps) {
  const hasProviders = providers.length > 0;
  const showPasswordForm = !hasProviders || passwordOpen;

  return (
    <Stack gap="md">
      {hasProviders && (
        <Stack gap="xs">
          {providers.map((p) => (
            <Button key={p.provider} variant="outline" fullWidth justify="flex-start" onClick={() => onOAuthSignIn(p)}>
              {PROVIDER_ICONS[p.icon] ? (
                <img src={PROVIDER_ICONS[p.icon]} alt="" style={{ height: 20, width: 20, marginRight: 12 }} />
              ) : null}
              Continue with {p.label}
            </Button>
          ))}
        </Stack>
      )}

      {showPasswordForm ? (
        <PasswordLoginForm onSubmit={onPasswordSubmit} isLoading={isLoading} autoFocus={hasProviders} />
      ) : (
        <Anchor
          component="button"
          type="button"
          fz="sm"
          c="dimmed"
          ta="center"
          data-testid="password-login-link"
          onClick={(e: { preventDefault: () => void }) => {
            e.preventDefault();
            onRevealPassword();
          }}
        >
          Sign in with password
        </Anchor>
      )}
    </Stack>
  );
}

function PasswordLoginForm({
  onSubmit,
  isLoading,
  autoFocus,
}: {
  onSubmit: (email: string, password: string) => void;
  isLoading: boolean;
  autoFocus: boolean;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(email, password);
      }}
    >
      <Stack gap="md">
        <TextInput
          label="Email"
          id="email"
          type="email"
          autoComplete="username"
          data-testid="email-input"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          placeholder="you@company.com"
          autoFocus={autoFocus}
          required
        />

        <PasswordInput
          label="Password"
          id="password"
          autoComplete="current-password"
          data-testid="password-input"
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
          required
        />

        <Button type="submit" disabled={isLoading} fullWidth data-testid="login-button" loading={isLoading}>
          Sign in
        </Button>
      </Stack>
    </form>
  );
}
